import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PaymentStatus {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

export function usePaymentStatuses() {
  const [paymentStatuses, setPaymentStatuses] = useState<PaymentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentStatuses = async () => {
    try {
      setLoading(true);
      setError(null);

      // First try to get from a dedicated payment_statuses table if it exists
      const { data: tableData, error: tableError } = await supabase
        .from("payment_statuses")
        .select("*")
        .order("name");

      if (tableData && tableData.length > 0) {
        // If dedicated table exists, use it
        setPaymentStatuses(tableData);
        return;
      }

      // Fallback: get distinct payment statuses from orders table
      const { data: orderData, error: orderError } = await supabase
        .from("orders")
        .select("payment_status")
        .not("payment_status", "is", null);

      if (orderError) {
        throw orderError;
      }

      // Extract unique payment statuses and create standardized objects
      const uniqueStatuses = [...new Set(orderData.map(item => item.payment_status))];
      
      const statusObjects: PaymentStatus[] = uniqueStatuses.map((status, index) => ({
        id: `status_${index}`,
        name: status,
        description: getStatusDescription(status),
        color: getStatusColor(status)
      }));

      setPaymentStatuses(statusObjects);
    } catch (err) {
      console.error("Error fetching payment statuses:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch payment statuses");
      
      // Fallback to default statuses if database query fails
      setPaymentStatuses(getDefaultPaymentStatuses());
    } finally {
      setLoading(false);
    }
  };

  const getStatusDescription = (status: string): string => {
    const descriptions: Record<string, string> = {
      pending: "Payment is awaiting confirmation",
      completed: "Payment has been successfully processed",
      cancelled: "Payment was cancelled",
      failed: "Payment processing failed",
      refunded: "Payment was refunded to customer",
      success: "Payment was successful"
    };
    return descriptions[status] || "Payment status";
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending: "yellow",
      completed: "green",
      cancelled: "red",
      failed: "red",
      refunded: "orange",
      success: "green"
    };
    return colors[status] || "gray";
  };

  const getDefaultPaymentStatuses = (): PaymentStatus[] => {
    return [
      { id: "pending", name: "pending", description: "Payment is awaiting confirmation", color: "yellow" },
      { id: "completed", name: "completed", description: "Payment has been successfully processed", color: "green" },
      { id: "cancelled", name: "cancelled", description: "Payment was cancelled", color: "red" },
      { id: "failed", name: "failed", description: "Payment processing failed", color: "red" },
      { id: "refunded", name: "refunded", description: "Payment was refunded to customer", color: "orange" }
    ];
  };

  useEffect(() => {
    fetchPaymentStatuses();
  }, []);

  const refreshPaymentStatuses = () => {
    fetchPaymentStatuses();
  };

  return {
    paymentStatuses,
    loading,
    error,
    refreshPaymentStatuses
  };
}
